import { useState, useEffect, useCallback, useRef } from 'react';
import { Timestamp } from 'firebase/firestore';
import { 
  saveDraft, 
  loadDraft, 
  deleteDraft as deleteDraftService,
  RegistrationDraft 
} from '../services/registrationDraftService';

interface UseRegistrationDraftOptions {
  eventId: string;
  odId: string;
  formData: Record<string, any>;
  currentStep: number;
  visitedSteps: Set<number>;
  enabled?: boolean;  // Allow disabling for editing existing registrations
  debounceMs?: number;
}

interface UseRegistrationDraftReturn {
  isDraftLoading: boolean;
  isDraftSaving: boolean;
  hasDraft: boolean;
  lastSaved: Date | null;
  loadedDraft: RegistrationDraft | null;
  clearDraft: () => Promise<void>;
  forceSave: () => Promise<void>;
  deleteDraft: () => Promise<void>;
}

export const useRegistrationDraft = ({
  eventId,
  odId,
  formData,
  currentStep,
  visitedSteps,
  enabled = true,
  debounceMs = 5000  // 5 second debounce as per plan
}: UseRegistrationDraftOptions): UseRegistrationDraftReturn => {
  const [isDraftLoading, setIsDraftLoading] = useState(false);
  const [isDraftSaving, setIsDraftSaving] = useState(false);
  const [hasDraft, setHasDraft] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [loadedDraft, setLoadedDraft] = useState<RegistrationDraft | null>(null);
  
  // Track analytics
  const analyticsRef = useRef<RegistrationDraft['analytics']>({
    navigationHistory: [],
    validationErrorCount: 0,
    sessionCount: 1,
    totalTimeSpentMs: 0,
    currentSessionStartedAt: Timestamp.now()
  });
  
  // Track previous form data for change detection
  const previousFormDataRef = useRef<string>('');
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const sessionStartRef = useRef<number>(Date.now());
  const isFinalizedRef = useRef<boolean>(false);
  
  // Load draft on mount
  useEffect(() => {
    if (!enabled || !eventId || !odId) return;
    
    const loadExistingDraft = async () => {
      setIsDraftLoading(true);
      try {
        const draft = await loadDraft(eventId, odId);
        if (draft) {
          setLoadedDraft(draft);
          setHasDraft(true);
          
          // Update analytics from loaded draft
          analyticsRef.current = {
            ...draft.analytics,
            sessionCount: (draft.analytics?.sessionCount || 0) + 1,
            totalTimeSpentMs: draft.analytics?.totalTimeSpentMs || 0,
            currentSessionStartedAt: Timestamp.now()
          };
        }
      } catch (error) {
        console.error('Error loading draft:', error);
      } finally {
        setIsDraftLoading(false);
      }
    };
    
    loadExistingDraft();
  }, [enabled, eventId, odId]);
  
  // Track navigation history
  const previousStepRef = useRef<number>(currentStep);
  useEffect(() => {
    if (previousStepRef.current !== currentStep) {
      analyticsRef.current.navigationHistory.push({
        from: previousStepRef.current,
        to: currentStep,
        timestamp: Timestamp.now()
      });
      previousStepRef.current = currentStep;
    }
  }, [currentStep]);
  
  // Debounced save function
  const debouncedSave = useCallback(async () => {
    if (!enabled || !eventId || !odId) return;
    if (isFinalizedRef.current) return;
    
    // Serialize form data for comparison
    const currentFormDataStr = JSON.stringify(formData);
    
    // Only save if data has changed
    if (currentFormDataStr === previousFormDataRef.current) {
      return;
    }
    
    previousFormDataRef.current = currentFormDataStr;
    
    // Update time spent
    const timeSpentThisSession = Date.now() - sessionStartRef.current;
    analyticsRef.current.totalTimeSpentMs += timeSpentThisSession;
    sessionStartRef.current = Date.now();
    
    setIsDraftSaving(true);
    try {
      await saveDraft(
        eventId,
        odId,
        formData,
        currentStep,
        Array.from(visitedSteps),
        analyticsRef.current
      );
      setHasDraft(true);
      setLastSaved(new Date());
    } catch (error) {
      console.error('Error saving draft:', error);
    } finally {
      setIsDraftSaving(false);
    }
  }, [enabled, eventId, odId, formData, currentStep, visitedSteps]);
  
  // Set up debounced auto-save on form data changes
  useEffect(() => {
    if (!enabled) return;
    
    // Clear previous timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    
    // Set new debounced save
    saveTimeoutRef.current = setTimeout(() => {
      debouncedSave();
    }, debounceMs);
    
    // Cleanup
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [formData, currentStep, visitedSteps, debouncedSave, debounceMs, enabled]);
  
  // Save on page unload
  useEffect(() => {
    if (!enabled) return;
    
    const handleBeforeUnload = () => {
      // Synchronous save attempt (may not complete)
      debouncedSave();
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [debouncedSave, enabled]);
  
  // Clear draft function (call on successful submission)
  const clearDraft = useCallback(async () => {
    if (!eventId || !odId) return;
    isFinalizedRef.current = true;
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = null;
    }
    
    try {
      await deleteDraftService(eventId, odId);
      setHasDraft(false);
      setLoadedDraft(null);
      setLastSaved(null);
    } catch (error) {
      console.error('Error clearing draft:', error);
    }
  }, [eventId, odId]);
  
  // Force save function (for immediate saves)
  const forceSave = useCallback(async () => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    await debouncedSave();
  }, [debouncedSave]);
  
  // Delete draft function (call on successful registration)
  const deleteDraft = useCallback(async () => {
    if (!eventId || !odId) return;
    isFinalizedRef.current = true;
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = null;
    }
    
    try {
      await deleteDraftService(eventId, odId);
      setHasDraft(false);
      setLoadedDraft(null);
      setLastSaved(null);
    } catch (error) {
      console.error('Error deleting draft:', error);
    }
  }, [eventId, odId]);
  
  return {
    isDraftLoading,
    isDraftSaving,
    hasDraft,
    lastSaved,
    loadedDraft,
    clearDraft,
    forceSave,
    deleteDraft
  };
};
